import { getHubUrl } from "@internal/auth";
import { useObservable } from "@/hooks/useObservable";
import { installPrompt } from "@/lib/installPrompt";

export function Innstillinger() {
  return (
    <div
      style={{
        maxWidth: 640,
        margin: "0 auto",
        padding: "24px var(--pad)",
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}
      data-testid="settings-screen"
    >
      <h1
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: 30,
          letterSpacing: -0.6,
          margin: 0,
        }}
      >
        Innstillinger
      </h1>

      <InstallSection />
      <AccountSection />
    </div>
  );
}

function AccountSection() {
  return (
    <Section title="Konto" testId="account-section">
      <a
        href={getHubUrl("/innstillinger")}
        target="_blank"
        rel="noopener"
        data-testid="min-konto-link"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 14,
          color: "var(--accent, var(--ink))",
          textDecoration: "none",
          fontWeight: 600,
        }}
      >
        Min konto
        <span aria-hidden style={{ fontSize: 12 }}>
          ↗
        </span>
      </a>
      <p
        style={{
          margin: "8px 0 0",
          fontSize: 13,
          color: "var(--ink-3)",
          lineHeight: 1.5,
        }}
      >
        Endre passord, e-post og tilganger.
      </p>
    </Section>
  );
}

function InstallSection() {
  const state = useObservable(installPrompt.state$, installPrompt.snapshot);
  if (state.installed) {
    return (
      <Section title="Installer app" testId="install-section">
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 14 }}>
          Appen er allerede installert.
        </p>
      </Section>
    );
  }
  return (
    <Section title="Installer app" testId="install-section">
      <p
        style={{
          margin: "0 0 14px",
          color: "var(--ink-2)",
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        Legg Dagbok til på hjem-skjermen for å åpne den raskt.
      </p>
      <InstallActions platform={state.platform} canPrompt={state.canPrompt} />
    </Section>
  );
}

function Section({
  title,
  children,
  testId,
}: {
  title: string;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <section data-testid={testId}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.8,
          color: "var(--ink-3)",
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      <div
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--rule)",
          borderRadius: "var(--radius)",
          padding: 18,
          boxShadow: "var(--shadow-sm)",
        }}
      >
        {children}
      </div>
    </section>
  );
}

function InstallActions({
  platform,
  canPrompt,
}: {
  platform: "chromium" | "ios-safari" | "generic";
  canPrompt: boolean;
}) {
  if (canPrompt && platform === "chromium") {
    return (
      <button
        type="button"
        className="btn-primary"
        data-testid="install-button"
        onClick={() => {
          void installPrompt.prompt();
        }}
      >
        Installer app
      </button>
    );
  }
  if (platform === "ios-safari") {
    return (
      <div
        data-testid="install-ios-instructions"
        style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.6 }}
      >
        Trykk på <IosShareGlyph /> i Safari-menyen, og velg{" "}
        <strong>«Legg til på Hjem-skjerm»</strong>.
      </div>
    );
  }
  return (
    <div
      data-testid="install-generic-instructions"
      style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.6 }}
    >
      Åpne appen i Chrome eller Edge for å installere den, eller bruk{" "}
      <strong>«Legg til på Hjem-skjerm»</strong> i nettlesermenyen.
    </div>
  );
}

function IosShareGlyph() {
  return (
    <span
      style={{
        display: "inline-flex",
        verticalAlign: "-3px",
        margin: "0 2px",
      }}
    >
      <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
        <title>Del-knappen i Safari</title>
        <path
          d="M8 2v11"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M4 6l4-4 4 4"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M2 9v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </span>
  );
}
